/**
 * Маршруты приложения — те же, что в `app/.../Navigation.kt` Android-версии.
 *
 * Экраны разных разделов не ссылаются друг на друга напрямую: переход «из задачи
 * в дело» — это вызов, который подставляет каркас, а не импорт чужого экрана
 * (ARCHITECTURE.md, правило 4). В вебе то же самое обеспечивает `check-rules.mjs`.
 */

export const routes = {
  cases: '/cases',
  tasks: '/tasks',
  clients: '/clients',
  calendar: '/calendar',
  search: '/search',
  settings: '/settings',
  /**
   * Каталог компонентов — инструмент разработчика, а не раздел приложения.
   * До шага 18 он занимал место «Настроек»; теперь у него свой адрес, и в нижнюю
   * панель он не попадает: юрист туда не ходит, а нам он нужен на устройстве.
   */
  catalogue: '/catalogue',
  quickScan: '/scan',

  caseDetail: (caseId: string) => `/case/${caseId}`,
  caseDocuments: (caseId: string) => `/case/${caseId}/documents`,
  client: (clientId: string) => `/client/${clientId}`,
  task: (taskId: string) => `/task/${taskId}`,
  event: (eventId: string) => `/event/${eventId}`,
  document: (documentId: string) => `/document/${documentId}`,
} as const;

/** Четыре раздела нижней панели. «Поиск» стоит рядом с ними — решение D-11. */
export type Tab = 'cases' | 'tasks' | 'clients' | 'calendar';

export const TABS: { tab: Tab; path: string; label: string; icon: string }[] = [
  { tab: 'cases', path: routes.cases, label: 'Дела', icon: 'folder' },
  { tab: 'tasks', path: routes.tasks, label: 'Задачи', icon: 'checklist' },
  { tab: 'clients', path: routes.clients, label: 'Клиенты', icon: 'group' },
  { tab: 'calendar', path: routes.calendar, label: 'Календарь', icon: 'calendar_month' },
];

/**
 * Раздел, которому принадлежит путь: карточка дела остаётся внутри «Дел».
 *
 * Иначе при переходе из списка в карточку подсветка в нижней панели гасла бы,
 * и юрист терял бы понимание, где находится.
 */
export function tabOf(path: string): Tab {
  if (path.startsWith('/cases') || path.startsWith('/case/')) return 'cases';
  if (path.startsWith('/clients') || path.startsWith('/client/')) return 'clients';
  if (path.startsWith('/calendar') || path.startsWith('/event/')) return 'calendar';
  if (path.startsWith('/tasks') || path.startsWith('/task/')) return 'tasks';

  return 'cases';
}

/** Разобранный путь: имя экрана и его параметр. */
export type Screen =
  | { name: 'cases' }
  | { name: 'tasks' }
  | { name: 'clients' }
  | { name: 'calendar' }
  | { name: 'search' }
  | { name: 'settings' }
  | { name: 'catalogue' }
  | { name: 'quickScan' }
  | { name: 'caseDetail'; caseId: string }
  | { name: 'caseDocuments'; caseId: string }
  | { name: 'client'; clientId: string }
  | { name: 'task'; taskId: string }
  | { name: 'event'; eventId: string }
  | { name: 'document'; documentId: string };

export function parse(path: string): Screen {
  const parts = path.split('?')[0]?.split('/').filter(Boolean) ?? [];
  const [first, second, third] = parts;

  switch (first) {
    case 'tasks':
      return { name: 'tasks' };
    case 'clients':
      return { name: 'clients' };
    case 'calendar':
      return { name: 'calendar' };
    case 'search':
      return { name: 'search' };
    case 'settings':
      return { name: 'settings' };
    case 'catalogue':
      return { name: 'catalogue' };
    case 'scan':
      return { name: 'quickScan' };
    case 'case':
      if (!second) break;
      return third === 'documents'
        ? { name: 'caseDocuments', caseId: second }
        : { name: 'caseDetail', caseId: second };
    case 'client':
      if (second) return { name: 'client', clientId: second };
      break;
    case 'task':
      if (second) return { name: 'task', taskId: second };
      break;
    case 'event':
      if (second) return { name: 'event', eventId: second };
      break;
    case 'document':
      if (second) return { name: 'document', documentId: second };
      break;
    default:
      break;
  }
  return { name: 'cases' };
}

/**
 * Где нижняя панель не показывается.
 *
 * Она видна во всех разделах, включая карточку дела и его документы — решение
 * владельца 01.09.2026 ([D-35](docs/DECISIONS.md)): из карточки дела часто уходят
 * в «Задачи», и раньше это стоило лишнего «Назад». Скрыта только там, где экран
 * занят одним делом целиком.
 */
export const hidesBottomBar = (screen: Screen) =>
  screen.name === 'document' || screen.name === 'quickScan';

/**
 * Где нет «плюса».
 *
 * Не везде, где панель: в документах дела своя кнопка «Добавить», и вторая круглая
 * рядом означала бы два разных «добавить» в одном углу. На поиске «плюс» закрыл бы
 * выдачу, ради которой экран открыт. В карточке дела и клиента он предлагал уйти
 * из записи, которую юрист только что открыл. На карточках задачи и события он вдобавок
 * **накрывал собой «Отменить»**: плашка отмены стоит у того же нижнего края. Поймано
 * прогоном 05.09.2026 — удалённую задачу нельзя было вернуть.
 */
export const hidesFab = (screen: Screen) =>
  hidesBottomBar(screen) ||
  screen.name === 'caseDetail' ||
  screen.name === 'caseDocuments' ||
  screen.name === 'client' ||
  screen.name === 'task' ||
  screen.name === 'event' ||
  screen.name === 'search' ||
  screen.name === 'settings' ||
  screen.name === 'catalogue';
